<template>
  <SelectableCard
    :item="adviser"
    :is-selected="isSelected"
    :tag="tag"
    class="adviser-card"
    @select="$emit('select', $event)"
  >
    <template v-slot:media>
      <AsyncImage
        :image="image"
        :show-images="showImages"
        :current-image-index-to-load="currentImageIndexToLoad"
        @load-next="$emit('load-next')"
        @is-ready="$emit('is-ready')"
      />
    </template>
    <template v-slot:overlayTopLeft>
      <Transition name="fade">
        <div v-if="hasVideo" class="selectable-card__video-badge">
          <AppIcon icon="video" class="align-self-center" />
        </div>
      </Transition>
    </template>
    <template v-slot:overlayTopRight>
      <Transition name="fade">
        <div v-if="isSelected" class="selectable-card__checkmark">
          <i class="gg-check-o"></i>
        </div>
      </Transition>
    </template>
    <template v-if="showSpokenLanguages" v-slot:overlayBottom>
      <Transition name="fade" mode="out-in">
        <AdviserSpokenLanguages
          v-if="showImages"
          :adviser-spoken-languages="adviser.spoken_languages"
        />
      </Transition>
    </template>
    <template v-slot:body>
      <div
        class="selectable-card-description"
        :class="{ 'selectable-card-description--with-action': showBiography }"
      >
        <div class="selectable-card-description__name">
          {{ adviser.name }}
        </div>
        <div
          class="selectable-card-description__info"
          :class="{ 'selectable-card-description__info--long': adviserInfo.length > 23 }"
        >
          <span>
            {{ adviserInfo }}
          </span>
        </div>
      </div>
      <div v-if="showBiography" class="selectable-card-action" @click.stop>
        <AdviserBiographyModal
          :adviser="adviser"
          :lang="lang"
          :is-selected="isSelected"
          @select="$emit('select', adviser)"
        />
      </div>
    </template>
  </SelectableCard>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue'
import { Adviser, LocalizedAdviser } from '~concierge/types/AppointmentTypes'
import { AsyncImageData } from '~conciergeUiCommons/composables/showImage'
import SelectableCard from '~conciergeUiCommons/components/AppointmentQuestionComponents/SelectableCard.vue'
import AsyncImage from '~conciergeUiCommons/components/AsyncImage.vue'
import AppIcon from '~conciergeUiCommons/components/AppIcon.vue'
import AdviserSpokenLanguages from '~conciergeUiCommons/components/AdviserSpokenLanguages.vue'
import AdviserBiographyModal from '~conciergeUiCommons/components/AdviserBiographyModal.vue'

const props = defineProps({
  adviser: {
    type: Object as PropType<Adviser & { info?: string | LocalizedAdviser }>,
    required: true
  },
  isSelected: {
    type: Boolean,
    required: true
  },
  tag: {
    type: String,
    default: 'div'
  },
  lang: {
    type: String,
    required: true
  },
  image: {
    type: Object as PropType<AsyncImageData | undefined>,
    required: true
  },
  showImages: {
    type: Boolean,
    default: false
  },
  currentImageIndexToLoad: {
    type: Number,
    default: 0
  },
  showSpokenLanguages: {
    type: Boolean,
    default: false
  },
  showBiography: {
    type: Boolean,
    default: false
  }
})

defineEmits(['select', 'load-next', 'is-ready'])

const adviserInfo = computed((): string => {
  const info = props.adviser.info
  if (typeof info !== 'string') {
    return info?.[props.lang as keyof LocalizedAdviser] ?? ''
  }
  return info
})

const hasVideo = computed((): boolean => {
  return (
    props.adviser.videos !== undefined &&
    props.adviser.videos[props.lang as keyof LocalizedAdviser] !== undefined &&
    props.adviser.videos[props.lang as keyof LocalizedAdviser] !== ''
  )
})
</script>
